import { Status, StatusCode } from "database";
import { FC, useCallback, useMemo, useState } from "react";
import { trpc } from "@utils";
import UpdateWalletAction from "@components/wallet/action-buttons/UpdateWalletAction";
import ActionsWrapper from "@components/wallet/action-buttons/ActionsWrapper";
import { useNotification } from "hooks/useNotification";
import DeleteAction from "@components/wallet/action-buttons/DeleteAction";

interface Props {
    status: Status;
    id: string;
    name: string;
}

const ExchangesActions: FC<Props> = ({ status, id, name }) => {
    const notify = useNotification();
    const [hasBeenClicked, setHasBeenClicked] = useState(false);
    const { invalidate: invalidateExchangeList } =
        trpc.useContext().exchangeWallet;

    const utils = trpc.useContext();

    const { mutate: invokeProcessing, isLoading } =
        trpc.exchangeWallet.invokeProcessing.useMutation({
            onSuccess: () => {
                invalidateExchangeList();
                notify.success("Started exchange processing");
            },
            onError: (error) => {
                notify.error(
                    error.data?.code === "FORBIDDEN"
                        ? error.message
                        : "Cannot process exchange at the moment ",
                );
            },
            onSettled: () => {
                setHasBeenClicked(false);
            },
        });

    const actionDisabled = useMemo(() => {
        return (
            status.code === StatusCode.RUNNING ||
            status.code === StatusCode.IDLE
        );
    }, [status.code]);

    const handleUpdate = useCallback(() => {
        setHasBeenClicked(true);

        invokeProcessing({ id, isHardReset: false });
    }, [id, invokeProcessing]);

    const deleteExternalSource = trpc.exchangeWallet.delete.useMutation({
        onMutate: () => {
            notify.loading("Deleting exchange wallet...");
        },
        onSuccess: () => {
            utils.exchangeWallet.list.invalidate();
            notify.success("Deleted exchange wallet successfuly");
        },
        onError: (err) => {
            notify.error(`Failed to delete exchange wallet : ${err.message}`);
        },
    });

    const handleDelete = useCallback(() => {
        deleteExternalSource.mutate({
            id,
        });
    }, [deleteExternalSource, id]);

    return (
        <ActionsWrapper>
            <UpdateWalletAction
                disabledText="Wait for the current processing to finish before re-processing the wallet"
                handleUpdate={handleUpdate}
                disabled={actionDisabled}
                isLoading={isLoading || hasBeenClicked}
            />

            <DeleteAction
                type="exchange"
                disabled={actionDisabled}
                handleDelete={handleDelete}
                name={name}
                description="Deleting this exchange will remove its data from future cost
                    basis calculations and reports. Historical reports will
                    remain unchanged. Do you wish to proceed?"
            />
        </ActionsWrapper>
    );
};

export default ExchangesActions;
